/** @jsx jsx */
import { Box, Button } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { jsx } from 'theme-ui';
import FacetValue from '../DefaultFacet/FacetValue';
import {
  cityToggled,
  countryToggled,
  distributionInCountriesToggled,
  distributionSoughtCountriesToggled,
  eventToggled,
  filtersCleared,
  offeringToggled,
  regionToggled,
  searchTermChanged,
} from './brandFilterReducer';
import { eventNames } from './customMappings';

/**
 * Returns the facets that are active
 * @param {object} param0 The state of the brand search.
 */
export function getActiveFacets({
  searchTerm,
  countries,
  regions,
  cities,
  events,
  distributionInCountries,
  distributionSoughtCountries,
  offerings,
}) {
  return [
    searchTerm,
    ...countries,
    ...cities,
    ...events,
    ...regions,
    ...distributionInCountries,
    ...distributionSoughtCountries,
    ...offerings,
  ].filter(Boolean);
}

/**
 * The facets bar on the top of the page. Selected facet values are shown here.
 * These shouldn't need to be customized – all the possible facets should be included though.
 * @param {*} param0
 */
export default function BrandFacetsBar({
  state,
  dispatch,
  activeFacets = getActiveFacets(state),
  children,
  ...rest
}) {
  const { i18n } = useLingui();
  if (activeFacets.length === 0) return null;
  const {
    searchTerm,
    events,
    countries,
    regions,
    cities,
    distributionInCountries,
    distributionSoughtCountries,
    offerings,
  } = state;
  return (
    <Box sx={{ padding: 3 }} {...rest}>
      <Box sx={{ marginBottom: -3 }}>
        {searchTerm && (
          <FacetValue
            isSelected
            onClick={() => dispatch(searchTermChanged(''))}
            variant="small"
          >
            {searchTerm}
          </FacetValue>
        )}
        {children}
        {offerings.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(offeringToggled(value))}
            variant="small"
          >
            {i18n._(`offering.${value}`)}
          </FacetValue>
        ))}
        {events.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(eventToggled(value))}
            variant="small"
          >
            {eventNames[value] || value}
          </FacetValue>
        ))}
        {countries.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(countryToggled(value))}
            variant="small"
          >
            {value}
          </FacetValue>
        ))}
        {regions.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(regionToggled(value))}
            variant="small"
          >
            {value}
          </FacetValue>
        ))}
        {cities.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(cityToggled(value))}
            variant="small"
          >
            {value}
          </FacetValue>
        ))}

        {distributionInCountries.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(distributionInCountriesToggled(value))}
            variant="small"
          >
            {value}
          </FacetValue>
        ))}
        {distributionSoughtCountries.map(value => (
          <FacetValue
            key={value}
            isSelected
            onClick={() => dispatch(distributionSoughtCountriesToggled(value))}
            variant="small"
          >
            {value}
          </FacetValue>
        ))}
        {activeFacets.length > 1 && (
          <Button
            variant="text"
            onClick={() => dispatch(filtersCleared())}
            sx={{ color: 'primary', variant: 'label.default' }}
          >
            <Trans>Clear all filters</Trans>
          </Button>
        )}
      </Box>
    </Box>
  );
}
